/** MY LISTS — each named list is its own placeable tile (Desk V3).
 * Items live in the unified desk_items model (type task + list_ref) so the
 * omnibar and Desk AI can file into a named list too. */
import React, { useEffect, useState, useRef, useCallback } from 'react';

const TXT = '#F1F2F3', SUB = '#A7ABB2', TEAL = '#54D197', AMBER = '#F79640', MAGENTA = '#EC7C24';
const BORDER = 'rgba(255,255,255,0.10)';
const mini = { fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, fontWeight: 700, color: TXT, background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 999, padding: '3px 10px', cursor: 'pointer' };
const inputStyle = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '7px 11px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: TXT, outline: 'none' };

export const DemoBadge = ({ seed }) => seed ? (
  <span data-testid="demo-badge" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 8, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: AMBER, border: `1px solid rgba(247,150,64,0.5)`, borderRadius: 4, padding: '0px 4px', flexShrink: 0 }}>demo</span>
) : null;

const DueChip = ({ due }) => {
  if (!due) return null;
  const past = due < new Date().toISOString().slice(0, 10);
  return <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, fontWeight: 700, color: past ? MAGENTA : SUB, border: `1px solid ${past ? 'rgba(236,124,36,0.4)' : BORDER}`, borderRadius: 999, padding: '1px 7px', flexShrink: 0 }}>📅 {due.slice(5)}</span>;
};

/** One named list as a tile: inline add (Enter=next), check→Win Wall + sweep,
 * inline edit, per-item due/tag/note, drag reorder, delete w/ inline confirm. */
export const ListTile = ({ listId, req, showToast, onChanged, tick, sweep, kind = 'list', demo = false, dm = (t) => t }) => {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(kind === 'project' ? 'board' : 'check');
  useEffect(() => { setView(kind === 'project' ? 'board' : 'check'); }, [kind]);
  const [addVal, setAddVal] = useState('');
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [winning, setWinning] = useState(null);
  const [colAdd, setColAdd] = useState({});
  const drag = useRef(null);
  const addRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await req('GET', '/api/admin/desk/lists');
      setItems(d.items[listId] || []);
    } catch { /* tile boundary handles hard crashes */ }
  }, [req, listId]);
  useEffect(() => { load(); }, [load, tick]);

  const addItem = async () => {
    const title = addVal.trim(); if (!title) return;
    setAddVal('');
    setItems((cur) => [...cur, { item_id: 'tmp-' + Date.now(), title, optimistic: true }]);
    await req('POST', '/api/admin/desk/items', { type: 'task', title, list_ref: listId, sort_order: items.length });
    await load();
    addRef.current?.focus();
  };
  const toggleDone = async (it, e) => {
    setWinning(it.item_id);
    sweep?.(e);
    setTimeout(() => setWinning(null), 750);
    await req('PATCH', `/api/admin/desk/items/${it.item_id}`, { state: 'done' });
    showToast(`🏆 "${it.title}" → Win Wall`, async () => {
      await req('PATCH', `/api/admin/desk/items/${it.item_id}`, { state: 'today' }); load(); onChanged?.();
    });
    setTimeout(() => { load(); onChanged?.(); }, 700);
  };
  const advance = async (it, e) => {
    const col = it.column || 'todo';
    if (col === 'done') return;
    const next = col === 'todo' ? 'doing' : 'done';
    if (next === 'done') { toggleDone(it, e); return; }
    setItems((cur) => cur.map((x) => x.item_id === it.item_id ? { ...x, column: next } : x));
    await req('PATCH', `/api/admin/desk/items/${it.item_id}`, { column: next });
    showToast(`→ "${it.title}" to Doing`, async () => {
      await req('PATCH', `/api/admin/desk/items/${it.item_id}`, { column: col }); load();
    });
    load();
  };
  const saveTitle = async () => {
    if (!editing) return;
    await req('PATCH', `/api/admin/desk/items/${editing.item_id}`, { title: editing.title });
    setEditing(null); load();
  };
  const saveDetail = async () => {
    if (!detail) return;
    await req('PATCH', `/api/admin/desk/items/${detail.item_id}`, {
      due_date: detail.due_date || null, client_tag: detail.client_tag || null, note: detail.note || null });
    setDetail(null); showToast('✓ Saved'); load();
  };
  const delItem = async (it) => {
    await req('DELETE', `/api/admin/desk/items/${it.item_id}?confirm=true`);
    setConfirmDel(null); showToast(`🗑 Deleted "${it.title}"`); load();
  };
  const onDropItem = async (targetIdx, e) => {
    const xfer = e?.dataTransfer?.getData('text/x-desk-item');
    const payload = xfer ? JSON.parse(xfer) : null;
    if (payload && payload.from !== listId) {
      // cross-list move — item changes home
      await req('PATCH', `/api/admin/desk/items/${payload.id}`, { list_ref: listId, sort_order: targetIdx == null ? items.length : targetIdx, column: null });
      drag.current = null;
      showToast(`→ Moved to this list`);
      load(); onChanged?.();
      return;
    }
    const d = drag.current; drag.current = null;
    if (!d) return;
    const moving = items.find((i) => i.item_id === d.id);
    if (!moving) return;
    const without = items.filter((i) => i.item_id !== d.id);
    const idx = targetIdx == null ? without.length : targetIdx;
    without.splice(idx, 0, moving);
    setItems(without);
    await req('POST', '/api/admin/desk/items/reorder', { list_id: listId, item_ids: without.map((i) => i.item_id) });
    load();
  };

  const row = (it, idx) => {
    if (it.optimistic) return (
      <div key={it.item_id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px', margin: '2px 0', borderRadius: 9, background: 'rgba(255,255,255,0.03)', opacity: 0.55 }}>
        <span style={{ width: 16, height: 16, borderRadius: '50%', border: `1.5px solid ${SUB}`, flexShrink: 0 }} />
        <p style={{ margin: 0, flex: 1, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: TXT }}>{dm(it.title)}</p>
      </div>
    );
    return (
    <div key={it.item_id} data-testid={`list-item-${it.item_id}`} draggable
      onDragStart={(e) => { drag.current = { id: it.item_id }; e.dataTransfer.setData('text/x-desk-item', JSON.stringify({ id: it.item_id, from: listId })); e.dataTransfer.effectAllowed = 'move'; }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropItem(idx, e); }}
      className={winning === it.item_id ? 'desk-win-pop desk-crossoff' : ''}
      style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px', margin: '2px 0', borderRadius: 9, background: 'rgba(255,255,255,0.03)', cursor: 'grab' }}>
      <button data-testid={`list-item-check-${it.item_id}`} onClick={(e) => toggleDone(it, e)} title="done"
        style={{ width: 16, height: 16, borderRadius: '50%', border: `1.5px solid ${TEAL}`, background: 'transparent', cursor: 'pointer', flexShrink: 0, padding: 0 }} />
      {editing?.item_id === it.item_id ? (
        <input autoFocus value={editing.title} data-testid={`list-item-edit-input-${it.item_id}`}
          onChange={(e) => setEditing({ ...editing, title: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && saveTitle()} onBlur={saveTitle}
          style={{ ...inputStyle, flex: 1, minWidth: 0, padding: '3px 8px' }} />
      ) : (
        <p data-testid={`list-item-title-${it.item_id}`} onClick={() => !demo && setEditing({ item_id: it.item_id, title: it.title })}
          style={{ margin: 0, flex: 1, minWidth: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: demo ? 'default' : 'text' }}>
          {dm(it.title)}
        </p>
      )}
      <DemoBadge seed={it.seed} />
      {it.client_tag && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: TEAL, flexShrink: 0 }}>@{dm(it.client_tag)}</span>}
      <DueChip due={it.due_date} />
      {it.note && <span title={demo ? 'note hidden' : it.note} style={{ fontSize: 10, flexShrink: 0 }}>📝</span>}
      <button data-testid={`list-item-more-${it.item_id}`} onClick={() => setDetail({ ...it })} style={{ ...mini, padding: '1px 7px', fontSize: 10, flexShrink: 0 }}>✎</button>
      {confirmDel === it.item_id ? (
        <button data-testid={`list-item-del-confirm-${it.item_id}`} onClick={() => delItem(it)} style={{ ...mini, color: MAGENTA, borderColor: 'rgba(236,124,36,0.5)', padding: '1px 7px', fontSize: 9.5, flexShrink: 0 }}>sure?</button>
      ) : (
        <button data-testid={`list-item-del-${it.item_id}`} onClick={() => { setConfirmDel(it.item_id); setTimeout(() => setConfirmDel((c) => c === it.item_id ? null : c), 3000); }}
          style={{ background: 'none', border: 'none', color: SUB, cursor: 'pointer', fontSize: 11, padding: 0, flexShrink: 0 }}>✕</button>
      )}
    </div>
    );
  };

  const moveColumn = async (it, col) => {
    setItems((cur) => cur.map((x) => x.item_id === it.item_id ? { ...x, column: col } : x));
    await req('PATCH', `/api/admin/desk/items/${it.item_id}`, { column: col });
    load();
  };
  const addToColumn = async (col) => {
    const title = (colAdd[col] || '').trim(); if (!title) return;
    setColAdd({ ...colAdd, [col]: '' });
    await req('POST', '/api/admin/desk/items', { type: 'task', title, list_ref: listId, column: col, sort_order: items.length });
    load();
  };

  const COLS = [['todo', 'To do', SUB], ['doing', 'Doing', AMBER], ['done', 'Done', TEAL]];

  const boardCard = (it) => (
    <div key={it.item_id} data-testid={`board-card-${it.item_id}`} draggable
      onDragStart={(e) => { drag.current = { id: it.item_id, card: it }; e.dataTransfer.setData('text/x-desk-item', JSON.stringify({ id: it.item_id, from: listId })); e.dataTransfer.effectAllowed = 'move'; }}
      className={winning === it.item_id ? 'desk-win-pop desk-crossoff' : ''}
      onClick={(e) => { if (e.target.closest('button,input')) return; advance(it, e); }}
      title={(it.column || 'todo') === 'todo' ? 'click → Doing' : (it.column || 'todo') === 'doing' ? 'click → Done 🏆' : ''}
      style={{ padding: '6px 8px', margin: '4px 0', borderRadius: 9, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, cursor: (it.column || 'todo') === 'done' ? 'grab' : 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button data-testid={`list-item-check-${it.item_id}`} onClick={(e) => toggleDone(it, e)} title="done → Win Wall"
          style={{ width: 14, height: 14, borderRadius: '50%', border: `1.5px solid ${TEAL}`, background: 'transparent', cursor: 'pointer', flexShrink: 0, padding: 0 }} />
        <p style={{ margin: 0, flex: 1, minWidth: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: TXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dm(it.title)}</p>
        <DemoBadge seed={it.seed} />
      </div>
      {(it.due_date || it.client_tag) && (
        <div style={{ display: 'flex', gap: 5, marginTop: 3 }}>
          {it.client_tag && <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, color: TEAL }}>@{it.client_tag}</span>}
          <DueChip due={it.due_date} />
        </div>
      )}
    </div>
  );

  return (
    <div className="no-drag" data-testid={`list-tile-${listId}`}
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onDropItem(null, e); }}
      style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, flexShrink: 0 }}>
        <input data-testid={`list-add-input-${listId}`} ref={addRef}
          placeholder="＋ Add item — Enter files it" value={addVal}
          onChange={(e) => setAddVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addItem()}
          style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
        <button data-testid={`list-view-toggle-${listId}`} onClick={() => setView(view === 'check' ? 'board' : 'check')}
          style={{ ...mini, padding: '1px 9px', fontSize: 9.5, color: AMBER, borderColor: 'rgba(247,150,64,0.4)', flexShrink: 0 }}>
          {view === 'check' ? '▦' : '☑'}
        </button>
      </div>
      {view === 'board' ? (
        <div data-testid={`list-board-${listId}`} style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8 }}>
          {COLS.map(([col, lbl, c]) => (
            <div key={col} data-testid={`board-col-${listId}-${col}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={async (e) => {
                e.preventDefault();
                const xfer = e.dataTransfer?.getData('text/x-desk-item');
                const payload = xfer ? JSON.parse(xfer) : null;
                if (payload && payload.from !== listId) {
                  drag.current = null;
                  await req('PATCH', `/api/admin/desk/items/${payload.id}`, { list_ref: listId, column: col });
                  showToast('→ Moved here'); load(); onChanged?.();
                  return;
                }
                const d = drag.current; drag.current = null;
                if (d?.card) moveColumn(d.card, col);
              }}
              style={{ display: 'flex', flexDirection: 'column', minHeight: 60, background: 'rgba(255,255,255,0.025)', border: `1px dashed ${BORDER}`, borderRadius: 10, padding: '6px 7px' }}>
              <p style={{ margin: '0 0 3px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: c }}>{lbl}</p>
              <div className="studio-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {items.filter((it) => (it.column || 'todo') === col).map(boardCard)}
              </div>
              <input data-testid={`board-col-add-${listId}-${col}`} placeholder="＋ card" value={colAdd[col] || ''}
                onChange={(e) => setColAdd({ ...colAdd, [col]: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && addToColumn(col)}
                style={{ ...inputStyle, padding: '4px 8px', fontSize: 10.5, marginTop: 4, flexShrink: 0, minWidth: 0 }} />
            </div>
          ))}
        </div>
      ) : (
        <div className="studio-scroll" data-testid={`list-items-${listId}`} style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 2 }}
             onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropItem(null, e); }}>
          {items.map((it, idx) => row(it, idx))}
          {items.length === 0 && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, margin: '4px 0' }}>Empty — type above, Enter files it.</p>}
        </div>
      )}

      {detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(8,9,11,0.8)', display: 'grid', placeItems: 'center' }} onClick={() => setDetail(null)}>
          <div data-testid="list-item-detail" onClick={(e) => e.stopPropagation()}
            style={{ width: 380, maxWidth: '92%', background: '#17181B', border: `1px solid ${BORDER}`, borderRadius: 16, padding: 18 }}>
            <p style={{ margin: '0 0 10px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: SUB }}>{detail.title}</p>
            <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB, display: 'block', marginBottom: 3 }}>Due date</label>
            <input type="date" data-testid="detail-due" value={detail.due_date || ''} onChange={(e) => setDetail({ ...detail, due_date: e.target.value })}
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: 8, colorScheme: 'dark' }} />
            <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB, display: 'block', marginBottom: 3 }}>Client tag</label>
            <input data-testid="detail-client" placeholder="e.g. T Touch" value={detail.client_tag || ''} onChange={(e) => setDetail({ ...detail, client_tag: e.target.value })}
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} />
            <label style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB, display: 'block', marginBottom: 3 }}>Note</label>
            <textarea data-testid="detail-note" rows={3} value={detail.note || ''} onChange={(e) => setDetail({ ...detail, note: e.target.value })}
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setDetail(null)} style={mini}>Cancel</button>
              <button data-testid="detail-save" onClick={saveDetail} style={{ ...mini, color: TEAL, borderColor: 'rgba(84,209,151,0.45)' }}>✓ Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
